import {
  createContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { userManager } from "../config/authConfig";
import { getData } from "../utils/apis";
import { sortMenuItems } from "../utils/menu";
import { useNotificationHandling } from "../utils/notificationHandle";

const staticDefaultUserValue = {
  practiceId: "",
  email: "",
  firstName: "",
  lastName: "",
  securityGroupMenus: [],
};

export const UserContext = createContext(staticDefaultUserValue);

function UserProvider({ children }) {
  const [user, setUser] = useState(staticDefaultUserValue);
  const [menuItems, setMenuItems] = useState([]);
  const [menus, setMenus] = useState([]);
  const [securityGroupMenus, setSecurityGroupMenus] = useState([]);
  const [defaultUserData, setDefaultUserData] = useState(
    staticDefaultUserValue
  );
  const [loading, setLoading] = useState(true);
  const securities = useRef([]);
  const securityMapRef = useRef(null);
  const { handleErrorNotification, handleWarningNotification } =
    useNotificationHandling();

  const fetchData = useCallback(
    async (endpoint, params = {}) => {
      try {
        const data = await getData(endpoint, params);
        return data;
      } catch (error) {
        handleErrorNotification(error);
        return endpoint === "practices" ? null : [];
      }
    },
    [handleErrorNotification]
  );

  const getUserGroups = useCallback(
    async (retries = 3, delay = 1000) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          const oidcUser = await userManager.getUser();
          return oidcUser?.profile["cognito:groups"];
        } catch (error) {
          if (attempt === retries) {
            handleErrorNotification(error);
            return null;
          }
          handleWarningNotification(
            "Connection Issue",
            `Loading user roles failed, retrying (${attempt}/${retries})...`
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    },
    [handleErrorNotification, handleWarningNotification]
  );

  const getSecurityGroupMenus = useCallback(
    () => fetchData("security_group_menus", { status: "Active" }),
    [fetchData]
  );

  const getMenus = useCallback(
    () => fetchData("menus", { status: "Active" }),
    [fetchData]
  );

  const getMenuItems = useCallback(
    () => fetchData("menu_items", { status: "Active" }),
    [fetchData]
  );

  const getPractice = useCallback(
    () => fetchData("practices", {}),
    [fetchData]
  );

  const updateMenuItems = useCallback(async () => {
    try {
      if (!securityMapRef.current?.size) return;

      const menuItems = await getMenuItems();
      const securityMapValues = Array.from(securityMapRef.current.values());
      const filteredMenuItems = menuItems.filter((menuItem) =>
        securityMapValues.includes(menuItem.menu)
      );

      setMenuItems(sortMenuItems(filteredMenuItems));
    } catch (error) {
      handleErrorNotification(error);
    }
  }, [getMenuItems, handleErrorNotification]);

  const updateMenus = useCallback(async () => {
    try {
      const [securityGroupMenusData, menusData] = await Promise.all([
        getSecurityGroupMenus(),
        getMenus(),
      ]);

      const filteredSecurityMenus = securityGroupMenusData.filter((menu) =>
        securities.current.includes(menu.security_group)
      );

      const securityMap = new Map(
        filteredSecurityMenus
          .filter((security) => security.read_permission)
          .map((security) => [security.menu_id, security.menu])
      );

      securityMapRef.current = securityMap;
      setSecurityGroupMenus(filteredSecurityMenus);

      const securityMapValues = Array.from(securityMap.values());
      const filteredMenus = menusData
        .filter((menu) => securityMapValues.includes(menu.name))
        .sort((a, b) => (a.menu_order < b.menu_order ? -1 : 1));

      setMenus(filteredMenus);
      await updateMenuItems();
    } catch (error) {
      handleErrorNotification(error);
    }
  }, [
    getSecurityGroupMenus,
    updateMenuItems,
    getMenus,
    handleErrorNotification,
  ]);

  const updateUserData = useCallback(async () => {
    const _securities = await getUserGroups();

    let practice = await getPractice();
    if (!practice?.[0]) {
      handleWarningNotification(
        "Connection Issue",
        "Loading practice data failed, retrying (1/3)..."
      );
      await new Promise((resolve) => setTimeout(resolve, 1000));
      practice = await getPractice();
    }
    if (!practice?.[0]) {
      handleWarningNotification(
        "Connection Issue",
        "Loading practice data failed, retrying (2/3)..."
      );
      await new Promise((resolve) => setTimeout(resolve, 2000));
      practice = await getPractice();
    }
    if (!practice?.[0]) {
      handleErrorNotification(new Error("Practice data is missing"));
      return;
    }

    securities.current = _securities;

    const oidcUser = await userManager.getUser();
    const userDataValues = {
      practice_name: practice[0].name,
      practiceId: oidcUser?.profile["custom:practice_id"],
      offices: oidcUser?.profile["custom:offices"],
      email: oidcUser?.profile.email,
      name: oidcUser?.profile.name,
      firstName: oidcUser?.profile.given_name,
      lastName: oidcUser?.profile.family_name,
      securities,
    };

    if (JSON.stringify(userDataValues) !== JSON.stringify(user)) {
      setUser(userDataValues);
      setDefaultUserData(userDataValues);
    }

    await updateMenus();
  }, [
    getPractice,
    getUserGroups,
    handleErrorNotification,
    handleWarningNotification,
    updateMenus,
    user,
  ]);

  const setUserData = useCallback(async () => {
    try {
      await updateUserData();
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, [updateUserData]);

  const userInfo = useMemo(
    () => ({
      ...defaultUserData,
      ...user,
      loading,
      menuItems,
      menus,
      securityGroupMenus,
      updateMenus,
      setUserData,
      updateMenuItems,
      setLoading,
    }),
    [
      defaultUserData,
      menuItems,
      menus,
      securityGroupMenus,
      loading,
      user,
      setUserData,
      updateMenus,
      updateMenuItems,
      setLoading,
    ]
  );

  useEffect(() => {
    const loadUserData = async () => {
      const oidcUser = await userManager.getUser();
      if (oidcUser && !oidcUser.expired) {
        try {
          await setUserData();
        } catch (error) {
          console.error(error);
        }
      }
    };

    loadUserData();

    const onUserLoaded = () => {
      loadUserData();
    };

    userManager.events.addUserLoaded(onUserLoaded);

    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
    };
  }, [setUserData]);

  return (
    <UserContext.Provider value={userInfo}>{children}</UserContext.Provider>
  );
}

export { UserProvider };
export default UserContext;
